import { useState, useEffect, useCallback } from 'react';

interface KeyboardState {
  isVisible: boolean;
  height: number;
  isInputFocused: boolean;
}

// Enhanced device detection
const isMobile = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
};

const isDesktop = () => {
  return typeof window !== 'undefined' && window.innerWidth >= 768;
};

const isIOS = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/i.test(userAgent);
};

export function useKeyboardAwareInput() {
  const [keyboardState, setKeyboardState] = useState<KeyboardState>({
    isVisible: false,
    height: 0,
    isInputFocused: false
  });

  // Set CSS custom property for keyboard height
  useEffect(() => {
    document.documentElement.style.setProperty('--keyboard-height', `${keyboardState.height}px`);
    
    return () => {
      // Cleanup on unmount
      document.documentElement.style.removeProperty('--keyboard-height');
    };
  }, [keyboardState.height]);

  // Enhanced debugging for mobile
  const debugLog = useCallback((message: string, data?: any) => {
    if (isMobile()) {
      console.log(`[KeyboardAware] ${message}`, data || '');
    }
  }, []);

  // Track input focus state
  const handleInputFocus = useCallback(() => {
    debugLog('Input focused');
    setKeyboardState(prev => ({ ...prev, isInputFocused: true }));
    
    // iOS specific: Force viewport update after a delay
    if (isIOS()) {
      setTimeout(() => {
        debugLog('iOS viewport force update');
        const currentHeight = window.visualViewport?.height || window.innerHeight;
        const initialHeight = window.screen.height;
        const keyboardHeight = Math.max(0, initialHeight - currentHeight);
        const isVisible = keyboardHeight > 100; // Higher threshold for iOS
        
        debugLog('iOS keyboard detection', { currentHeight, initialHeight, keyboardHeight, isVisible });
        
        setKeyboardState(prev => ({
          ...prev,
          isVisible,
          height: keyboardHeight
        }));
      }, 300); // iOS keyboard animation delay
    }
  }, [debugLog]);

  const handleInputBlur = useCallback(() => {
    debugLog('Input blurred');
    setKeyboardState(prev => ({ ...prev, isInputFocused: false }));
  }, [debugLog]);

  // Enhanced keyboard detection for iOS Safari/Chrome
  useEffect(() => {
    let initialHeight = window.innerHeight;
    let initialViewportHeight = window.visualViewport?.height || window.innerHeight;
    
    debugLog('Initial setup', { 
      innerHeight: initialHeight, 
      viewportHeight: initialViewportHeight,
      isMobile: isMobile(),
      isIOS: isIOS(),
      userAgent: navigator.userAgent
    });
    
    const handleViewportChange = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      const keyboardHeight = Math.max(0, initialViewportHeight - currentHeight);
      const threshold = isIOS() ? 100 : 50; // Higher threshold for iOS
      const isVisible = keyboardHeight > threshold;
      
      debugLog('Viewport change', { 
        currentHeight, 
        initialViewportHeight, 
        keyboardHeight, 
        threshold, 
        isVisible 
      });
      
      setKeyboardState(prev => ({
        ...prev,
        isVisible,
        height: keyboardHeight
      }));
    };

    // iOS Safari specific handling
    const handleIOSResize = () => {
      const currentHeight = window.innerHeight;
      const heightDiff = initialHeight - currentHeight;
      
      debugLog('iOS resize event', { 
        currentHeight, 
        initialHeight, 
        heightDiff 
      });
      
      // iOS specific logic: keyboard appears when height decreases significantly
      if (heightDiff > 150) {
        const keyboardHeight = heightDiff;
        const isVisible = true;
        
        debugLog('iOS keyboard detected', { keyboardHeight, isVisible });
        
        setKeyboardState(prev => ({
          ...prev,
          isVisible,
          height: keyboardHeight
        }));
      } else if (heightDiff < 50) {
        debugLog('iOS keyboard hidden');
        setKeyboardState(prev => ({
          ...prev,
          isVisible: false,
          height: 0
        }));
      }
    };

    if (window.visualViewport && !isIOS()) {
      // Modern approach - works well on Android Chrome
      debugLog('Using Visual Viewport API');
      window.visualViewport.addEventListener('resize', handleViewportChange);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
      };
    } else {
      // iOS Safari fallback
      debugLog('Using window resize fallback (iOS)');
      window.addEventListener('resize', handleIOSResize);
      return () => {
        window.removeEventListener('resize', handleIOSResize);
      };
    }
  }, [debugLog]);

  // Handle escape key to dismiss
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && keyboardState.isInputFocused) {
        debugLog('Escape key pressed');
        // Find focused input and blur it
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement.blur) {
          activeElement.blur();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [keyboardState.isInputFocused, debugLog]);

  // Calculate pixel-based transform offset using detected keyboard height
  const getInputTransform = useCallback(() => {
    if (keyboardState.isInputFocused && keyboardState.isVisible && keyboardState.height > 0) {
      const transform = `translateY(-${keyboardState.height}px)`;
      debugLog('Pixel transform calculated', { 
        isInputFocused: keyboardState.isInputFocused, 
        isVisible: keyboardState.isVisible,
        keyboardHeight: keyboardState.height,
        transform 
      });
      return transform;
    }
    return 'translateY(0)';
  }, [keyboardState.isInputFocused, keyboardState.isVisible, keyboardState.height, debugLog]);

  // Overlay backdrop when input is focused
  const shouldShowOverlay = keyboardState.isInputFocused && keyboardState.isVisible;
  
  debugLog('Overlay state', { shouldShowOverlay, keyboardState });

  return {
    keyboardState,
    getInputTransform,
    shouldShowOverlay,
    handleInputFocus,
    handleInputBlur,
    isMobile: isMobile(),
    isDesktop: isDesktop(),
    isIOS: isIOS(),
    // Export keyboard height for direct use
    keyboardHeight: keyboardState.height
  };
}